import axios from 'axios';
import { restoreSession, persistSession, clearSession } from '../utils/tokenStore';
import { withServerMessage } from '../utils/apiErrorMessage';

// Configuration axios partagée avec l'URL de base
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL || 'http://localhost:4000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// iOS PWA: localStorage writes can be LOST on force-kill (async flush). Restore
// the session from the cookie mirror before any request goes out.
restoreSession();

// Flag pour éviter les redirections multiples simultanées
let isRedirecting = false;

// Public guest pages: /guestbook/…, /checkin/…, /payment/…, /payment-success…, and their
// /:companyName/… variants. Dashboard pages (/dashboard/checkin-builder…) never match.
const GUEST_PATH = /^(\/(?!dashboard\b)[^/]+)?\/(guestbook|checkin|payment)\/|^\/(payment|guestbook)-[a-z]+(\/|$)/;
const isGuestSurface = () => GUEST_PATH.test(window.location.pathname);

// Single-flight refresh: on app resume, SWR fires many requests at once — if
// the token needs renewing they ALL 401 together. One shared promise ensures a
// single /auth/refresh round-trip; every 401'd request awaits the same result.
let refreshPromise = null;
const refreshToken = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${apiClient.defaults.baseURL}/auth/refresh`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((res) => {
        const fresh = res.data?.token;
        if (!fresh) throw new Error('no token in refresh response');
        persistSession(fresh); // localStorage + cookie mirror
        return fresh;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Log si une requête authentifiée est faite sans token
    console.warn('⚠️ API Request without token:', config.url);
  }
  return config;
});

// Intercepteur pour gérer les erreurs 401 (token expiré)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Guest tracking: if a request to a guest-facing route fails, log it as an
    // api_failure attributed to the current guest page (set by initGuestSession).
    try {
      const url = error.config?.url || '';
      if (/\/(checkin|guestbook)\b/.test(url)) {
        // Lazy import to avoid a circular dependency at module load.
        // eslint-disable-next-line global-require
        const { getGuestContext, trackGuestError } = require('../utils/guestTracking');
        const ctx = getGuestContext();
        if (ctx.reservationId) {
          trackGuestError(ctx.reservationId, ctx.surface, 'api_failure', error.response?.data?.message || error.message, {
            endpoint: url,
            statusCode: error.response?.status,
          });
        }
      }
    } catch {
      /* never break the error flow */
    }

    // Guest surfaces (guest app, check-in, payment) have no dashboard session:
    // a 401 there must never send the guest to the Hostea login page.
    if (error.response?.status === 401 && isGuestSurface()) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // Log pour debug
      console.warn('🔐 401 Unauthorized:', {
        url: error.config?.url,
        message: error.response?.data?.message,
        hasToken: !!localStorage.getItem('token')
      });

      const original = error.config || {};
      const isRefreshCall = (original.url || '').includes('/auth/refresh');
      const hadToken = !!localStorage.getItem('token');

      // A 401 is NOT an immediate logout anymore. iOS PWAs get frozen/killed and
      // resurrected constantly; a transient 401 at resume used to wipe the whole
      // session. Instead: try ONE silent refresh (exchange the still-valid token
      // for a fresh 30d one), then replay the original request. Only a refresh
      // that itself fails (token truly expired/invalid) logs the user out.
      if (hadToken && !isRefreshCall && !original._retried && !window.location.pathname.includes('/login')) {
        original._retried = true;
        return refreshToken()
          .then((fresh) => {
            original.headers = { ...original.headers, Authorization: `Bearer ${fresh}` };
            return apiClient(original); // replay the original request
          })
          .catch(() => {
            // Refresh refused → the session is genuinely dead. Logout for real.
            if (!isRedirecting && !window.location.pathname.includes('/login')) {
              isRedirecting = true;
              clearSession();
              window.location.href = '/login';
            }
            return Promise.reject(error);
          });
      }

      // No token / refresh path itself / already retried → hard logout as before.
      if (!isRedirecting && !window.location.pathname.includes('/login')) {
        isRedirecting = true;
        clearSession();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Last in the chain: errors carry the server's reason as `message` (see apiErrorMessage)
apiClient.interceptors.response.use(undefined, withServerMessage);

export default apiClient;
